<template>
  <div class="goods-management">
    <div class="page-header">
      <h2>商品管理</h2>
      <el-button type="primary" @click="showCreateDialog">新增商品</el-button>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="商品名称">
        <el-input v-model="searchForm.goods_name" placeholder="请输入商品名称" clearable />
      </el-form-item>
      <el-form-item label="分类">
        <el-select v-model="searchForm.category_id" placeholder="请选择分类" clearable>
          <el-option
            v-for="category in categories"
            :key="category.id"
            :label="category.category_name"
            :value="category.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="店铺">
        <el-select v-model="searchForm.shop_id" placeholder="请选择店铺" clearable>
          <el-option
            v-for="shop in shops"
            :key="shop.id"
            :label="shop.shop_name"
            :value="shop.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
          <el-option label="上架" :value="1" />
          <el-option label="下架" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="goodsList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="goods_name" label="商品名称" />
      <el-table-column prop="goods_images" label="商品图片" width="100">
        <template slot-scope="scope">
          <el-image v-if="scope.row.goods_images" :src="firstImage(scope.row.goods_images)" style="width: 60px; height: 60px" fit="cover" />
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="shop_name" label="店铺" width="120" />
      <el-table-column prop="category_name" label="分类" width="120" />
      <el-table-column prop="price" label="价格" width="100">
        <template slot-scope="scope">
          ¥{{ scope.row.price }}
        </template>
      </el-table-column>
      <el-table-column prop="original_price" label="原价" width="100">
        <template slot-scope="scope">
          ¥{{ scope.row.original_price || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="stock" label="库存" width="80" />
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status == 1 ? 'success' : 'danger'">
            {{ scope.row.status == 1 ? '上架' : '下架' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="1000px">
      <el-form ref="goodsForm" :model="goodsForm" :rules="goodsRules" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商品名称" prop="goods_name">
              <el-input v-model="goodsForm.goods_name" placeholder="请输入商品名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商品副标题">
              <el-input v-model="goodsForm.goods_sub_title" placeholder="请输入商品副标题" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商品编码">
              <el-input v-model="goodsForm.goods_code" placeholder="请输入商品编码" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属店铺" prop="shop_id">
              <el-select v-model="goodsForm.shop_id" placeholder="请选择店铺" style="width: 100%">
                <el-option
                  v-for="shop in shops"
                  :key="shop.id"
                  :label="shop.shop_name"
                  :value="shop.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商品分类" prop="category_id">
              <el-select v-model="goodsForm.category_id" placeholder="请选择分类" style="width: 100%">
                <el-option
                  v-for="category in categories"
                  :key="category.id"
                  :label="category.category_name"
                  :value="category.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <!-- 规格类型已移除，系统默认使用单规格 -->
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="销售价格" prop="price">
              <el-input-number v-model="goodsForm.price" :min="0" :precision="2" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="原价">
              <el-input-number v-model="goodsForm.original_price" :min="0" :precision="2" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="会员价">
              <el-input-number v-model="goodsForm.cost_price" :min="0" :precision="2" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="库存数量" prop="stock">
              <el-input-number v-model="goodsForm.stock" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="最低库存预警">
              <el-input-number v-model="goodsForm.min_stock" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="商品状态" prop="status">
              <el-radio-group v-model="goodsForm.status">
                <el-radio :label="1">上架</el-radio>
                <el-radio :label="0">下架</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="重量(kg)">
              <el-input-number v-model="goodsForm.weight" :min="0" :precision="3" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="体积(立方米)">
              <el-input-number v-model="goodsForm.volume" :min="0" :precision="3" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="运费">
              <el-input-number v-model="goodsForm.shipping_fee" :min="0" :precision="2" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="商品图片" prop="goods_images">
          <div class="goods-images-upload">
            <el-upload
              class="images-uploader"
              :action="uploadAction"
              :file-list="goodsImagesList"
              list-type="picture-card"
              :before-upload="beforeGoodsImagesUpload"
              :on-remove="handleRemoveImage"
              :headers="uploadHeaders"
              multiple
              :limit="9"
            >
              <i class="el-icon-plus"></i>
            </el-upload>
            <div class="upload-tips">
              <p>支持 JPG、PNG、GIF、WEBP 格式</p>
              <p>单张图片大小不超过 5MB</p>
              <p>最多上传 9 张图片</p>
              <p>建议尺寸：800x800 像素</p>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="商品视频">
          <div class="video-upload-container">
            <el-upload
              class="video-uploader"
              :action="uploadAction"
              :show-file-list="false"
              :before-upload="beforeVideoUpload"
              :headers="uploadHeaders"
            >
              <video v-if="goodsForm.goods_video" :src="goodsForm.goods_video" class="video-preview" controls></video>
              <i v-else class="el-icon-plus video-uploader-icon"></i>
            </el-upload>
            <div class="video-upload-tips">
              <p>支持 MP4、AVI、MOV 格式</p>
              <p>视频大小不超过 50MB</p>
              <p>建议时长：15-60 秒</p>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="商品描述" prop="goods_desc">
          <el-input v-model="goodsForm.goods_desc" type="textarea" :rows="3" placeholder="请输入商品描述" />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="是否推荐">
              <el-radio-group v-model="goodsForm.is_recommend">
                <el-radio :label="1">是</el-radio>
                <el-radio :label="0">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="是否新品">
              <el-radio-group v-model="goodsForm.is_new">
                <el-radio :label="1">是</el-radio>
                <el-radio :label="0">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="是否热销">
              <el-radio-group v-model="goodsForm.is_hot">
                <el-radio :label="1">是</el-radio>
                <el-radio :label="0">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="是否精品">
              <el-radio-group v-model="goodsForm.is_best">
                <el-radio :label="1">是</el-radio>
                <el-radio :label="0">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="是否包邮">
              <el-radio-group v-model="goodsForm.free_shipping">
                <el-radio :label="1">是</el-radio>
                <el-radio :label="0">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="排序">
              <el-input-number v-model="goodsForm.sort_order" :min="0" :max="9999" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="标签">
          <el-input v-model="goodsForm.tags" placeholder="请输入标签，多个用逗号分隔" />
        </el-form-item>
        <el-form-item label="SEO标题">
          <el-input v-model="goodsForm.seo_title" placeholder="请输入SEO标题" />
        </el-form-item>
        <el-form-item label="SEO关键词">
          <el-input v-model="goodsForm.seo_keywords" placeholder="请输入SEO关键词，多个用逗号分隔" />
        </el-form-item>
        <el-form-item label="SEO描述">
          <el-input v-model="goodsForm.seo_desc" type="textarea" :rows="2" placeholder="请输入SEO描述" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog title="商品详情" :visible.sync="viewDialogVisible" width="900px">
      <div v-if="currentGoods" class="goods-detail">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">商品ID</div>
              <div class="detail-value">{{ currentGoods.id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">商品名称</div>
              <div class="detail-value">{{ currentGoods.goods_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">商品副标题</div>
              <div class="detail-value">{{ currentGoods.goods_sub_title || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">商品编码</div>
              <div class="detail-value">{{ currentGoods.goods_code || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">所属店铺</div>
              <div class="detail-value">{{ currentGoods.shop_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">商品分类</div>
              <div class="detail-value">{{ currentGoods.category_name }}</div>
            </div>
            <!-- 规格类型已移除 -->
            <div class="detail-row">
              <div class="detail-label">商品状态</div>
              <div class="detail-value">
                <el-tag :type="currentGoods.status == 1 ? 'success' : 'danger'">
                  {{ currentGoods.status == 1 ? '上架' : '下架' }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- 价格库存信息 -->
        <div class="detail-section">
          <div class="section-title">价格库存信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">销售价格</div>
              <div class="detail-value">
                <span class="price">¥{{ currentGoods.price }}</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">原价</div>
              <div class="detail-value">
                <span class="original-price">¥{{ currentGoods.original_price || '-' }}</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">会员价</div>
              <div class="detail-value">¥{{ currentGoods.cost_price || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">库存数量</div>
              <div class="detail-value">{{ currentGoods.stock }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">最低库存预警</div>
              <div class="detail-value">{{ currentGoods.min_stock || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">销量</div>
              <div class="detail-value">
                <span class="stat-value">{{ currentGoods.sales_count || 0 }}</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">浏览量</div>
              <div class="detail-value">
                <span class="stat-value">{{ currentGoods.view_count || 0 }}</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">收藏量</div>
              <div class="detail-value">
                <span class="stat-value">{{ currentGoods.collect_count || 0 }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 商品属性 -->
        <div class="detail-section">
          <div class="section-title">商品属性</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">重量(kg)</div>
              <div class="detail-value">{{ currentGoods.weight || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">体积(立方米)</div>
              <div class="detail-value">{{ currentGoods.volume || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">运费</div>
              <div class="detail-value">¥{{ currentGoods.shipping_fee || 0 }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">是否包邮</div>
              <div class="detail-value">
                <el-tag :type="currentGoods.free_shipping == 1 ? 'success' : 'info'" size="small">
                  {{ currentGoods.free_shipping == 1 ? '是' : '否' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">是否推荐</div>
              <div class="detail-value">
                <el-tag :type="currentGoods.is_recommend == 1 ? 'success' : 'info'" size="small">
                  {{ currentGoods.is_recommend == 1 ? '是' : '否' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">是否新品</div>
              <div class="detail-value">
                <el-tag :type="currentGoods.is_new == 1 ? 'success' : 'info'" size="small">
                  {{ currentGoods.is_new == 1 ? '是' : '否' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">是否热销</div>
              <div class="detail-value">
                <el-tag :type="currentGoods.is_hot == 1 ? 'success' : 'info'" size="small">
                  {{ currentGoods.is_hot == 1 ? '是' : '否' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">是否精品</div>
              <div class="detail-value">
                <el-tag :type="currentGoods.is_best == 1 ? 'success' : 'info'" size="small">
                  {{ currentGoods.is_best == 1 ? '是' : '否' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">排序</div>
              <div class="detail-value">{{ currentGoods.sort_order || 0 }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">标签</div>
              <div class="detail-value">{{ currentGoods.tags || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 商品图片 -->
        <div class="detail-section">
          <div class="section-title">商品图片</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">商品图片</div>
              <div class="detail-value">
                <div v-if="currentGoods.goods_images" class="goods-images">
                  <el-image 
                    v-for="(img, index) in getGoodsImages"
                    :key="index"
                    :src="img" 
                    style="width: 100px; height: 100px; border-radius: 4px; cursor: pointer; margin-right: 10px; margin-bottom: 10px;"
                    fit="cover"
                    :preview-src-list="getGoodsImages"
                  />
                </div>
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">商品视频</div>
              <div class="detail-value">
                <a v-if="currentGoods.goods_video" :href="currentGoods.goods_video" target="_blank">查看视频</a>
                <span v-else>-</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 商品描述 -->
        <div class="detail-section">
          <div class="section-title">商品描述</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">商品描述</div>
              <div class="detail-value">{{ currentGoods.goods_desc || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- SEO信息 -->
        <div class="detail-section">
          <div class="section-title">SEO信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">SEO标题</div>
              <div class="detail-value">{{ currentGoods.seo_title || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">SEO关键词</div>
              <div class="detail-value">{{ currentGoods.seo_keywords || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">SEO描述</div>
              <div class="detail-value">{{ currentGoods.seo_desc || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 其他信息 -->
        <div class="detail-section">
          <div class="section-title">其他信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">创建时间</div>
              <div class="detail-value">{{ currentGoods.create_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">更新时间</div>
              <div class="detail-value">{{ currentGoods.update_time || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEdit(currentGoods)">编辑商品</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getGoodsList, getGoodsDetail, createGoods, updateGoods, deleteGoods, getShopList } from '@/api/store'
import { getCategoryList } from '@/api/store'
import { uploadImage, uploadImages, uploadVideo } from '@/api/upload'
import { toImageList, firstImage } from '@/utils/images'

export default {
  name: 'GoodsManagement',
  computed: {
    getGoodsImages() {
      if (!this.currentGoods) return []
      return toImageList(this.currentGoods.goods_images)
    },
    goodsImagesList() {
      if (!this.goodsForm.goods_images) return []
      return toImageList(this.goodsForm.goods_images).map((url, index) => ({
        uid: index,
        url: url
      }))
    }
  },
  data() {
    return {
      loading: false,
      goodsList: [],
      categories: [],
      shops: [],
      searchForm: {
        goods_name: '',
        category_id: '',
        shop_id: '',
        status: ''
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dialogVisible: false,
      viewDialogVisible: false,
      dialogTitle: '新增商品',
      goodsForm: {
        id: null,
        goods_name: '',
        goods_sub_title: '',
        goods_code: '',
        shop_id: '',
        shop_name: '',
        category_id: '',
        category_name: '',
        brand_id: '',
        brand_name: '',
        goods_images: '',
        goods_video: '',
        goods_desc: '',
        spec_type: 1,
        price: 0,
        original_price: 0,
        cost_price: 0,
        stock: 0,
        min_stock: 0,
        weight: 0,
        volume: 0,
        shipping_fee: 0,
        free_shipping: 1,
        status: 1,
        is_recommend: 0,
        is_new: 0,
        is_hot: 0,
        is_best: 0,
        sort_order: 100,
        tags: '',
        seo_title: '',
        seo_keywords: '',
        seo_desc: ''
      },
      currentGoods: null,
      goodsRules: {
        goods_name: [
          { required: true, message: '请输入商品名称', trigger: 'blur' },
          { min: 2, max: 200, message: '长度在 2 到 200 个字符', trigger: 'blur' }
        ],
        shop_id: [
          { required: true, message: '请选择所属店铺', trigger: 'change' }
        ],
        category_id: [
          { required: true, message: '请选择商品分类', trigger: 'change' }
        ],
        price: [
          { required: true, message: '请输入商品价格', trigger: 'blur' }
        ],
        stock: [
          { required: true, message: '请输入商品库存', trigger: 'blur' }
        ]
      },
      // 上传相关
      uploadAction: '',
      uploadHeaders: {},
      uploadLoading: null
    }
  },
  mounted() {
    this.fetchGoodsList()
    this.fetchCategories()
    this.fetchShops()
    this.initUpload()
  },
  methods: {
    // 模板里取首图用(兼容 JSON 数组 / 逗号分隔 / 单地址三种格式)
    firstImage,

    // 初始化上传配置
    initUpload() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      this.uploadHeaders = {
        'Authorization': `Bearer ${token}`
      }
      this.uploadAction = ''
    },
    
    // 商品图片上传前验证
    beforeGoodsImagesUpload(file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      const isImage = allowedTypes.includes(file.type)
      if (!isImage) {
        this.$message.error('只能上传 JPG、PNG、GIF、WEBP 格式的图片！')
        return false
      }
      
      const maxSize = 5 * 1024 * 1024 // 5MB
      const isLt5M = file.size <= maxSize
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB！')
        return false
      }
      
      this.uploadLoading = this.$loading({
        lock: true,
        text: '上传中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      this.handleGoodsImageUpload(file)
      return false
    },
    
    // 商品图片上传
    async handleGoodsImageUpload(file) {
      try {
        const response = await uploadImage(file, 'admin/goods/images')
        
        if (response.code === 200) {
          // 获取当前图片列表(兼容多种历史格式)
          const images = toImageList(this.goodsForm.goods_images)

          // 添加新图片
          images.push(response.data.url)
          this.goodsForm.goods_images = JSON.stringify(images)
          this.$message.success('图片上传成功！')
        } else {
          this.$message.error(response.msg || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error('上传失败，请重试')
      } finally {
        if (this.uploadLoading) {
          this.uploadLoading.close()
        }
      }
    },
    
    // 移除商品图片
    handleRemoveImage(file) {
      try {
        let images = toImageList(this.goodsForm.goods_images)

        // 移除指定图片
        images = images.filter(url => url !== file.url)
        this.goodsForm.goods_images = JSON.stringify(images)
      } catch (e) {
        console.error('移除图片失败:', e)
      }
    },
    
    // 视频上传前验证
    beforeVideoUpload(file) {
      const allowedTypes = ['video/mp4', 'video/avi', 'video/quicktime', 'video/x-msvideo']
      const isVideo = allowedTypes.includes(file.type)
      if (!isVideo) {
        this.$message.error('只能上传 MP4、AVI、MOV 格式的视频！')
        return false
      }
      
      const maxSize = 50 * 1024 * 1024 // 50MB
      const isLt50M = file.size <= maxSize
      if (!isLt50M) {
        this.$message.error('视频大小不能超过 50MB！')
        return false
      }
      
      this.uploadLoading = this.$loading({
        lock: true,
        text: '视频上传中，请稍候...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      this.handleVideoUpload(file)
      return false
    },
    
    // 视频上传
    async handleVideoUpload(file) {
      try {
        const response = await uploadVideo(file, 'admin/goods/videos')
        
        if (response.code === 200) {
          this.goodsForm.goods_video = response.data.url
          this.$message.success('视频上传成功！')
        } else {
          this.$message.error(response.msg || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error('上传失败，请重试')
      } finally {
        if (this.uploadLoading) {
          this.uploadLoading.close()
        }
      }
    },
    
    async fetchGoodsList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.searchForm
        }
        const response = await getGoodsList(params)
        this.goodsList = response.data.list || []
        this.pagination.total = response.data.total || 0
      } catch (error) {
        console.error('获取商品列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    async fetchCategories() {
      try {
        const response = await getCategoryList({ page: 1, limit: 1000 })
        this.categories = response.data.list || []
      } catch (error) {
        console.error('获取分类列表失败:', error)
      }
    },
    async fetchShops() {
      try {
        const response = await getShopList({ page: 1, limit: 1000 })
        this.shops = response.data.list || []
      } catch (error) {
        console.error('获取店铺列表失败:', error)
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchGoodsList()
    },
    handleReset() {
      this.searchForm = {
        goods_name: '',
        category_id: '',
        shop_id: '',
        status: ''
      }
      this.pagination.page = 1
      this.fetchGoodsList()
    },
    handleSizeChange(size) {
      this.pagination.limit = size
      this.fetchGoodsList()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchGoodsList()
    },
    showCreateDialog() {
      this.dialogTitle = '新增商品'
      this.goodsForm = {
        id: null,
        goods_name: '',
        goods_sub_title: '',
        goods_code: '',
        shop_id: '',
        shop_name: '',
        category_id: '',
        category_name: '',
        brand_id: '',
        brand_name: '',
        goods_images: '',
        goods_video: '',
        goods_desc: '',
        spec_type: 1,
        price: 0,
        original_price: 0,
        cost_price: 0,
        stock: 0,
        min_stock: 0,
        weight: 0,
        volume: 0,
        shipping_fee: 0,
        free_shipping: 1,
        status: 1,
        is_recommend: 0,
        is_new: 0,
        is_hot: 0,
        is_best: 0,
        sort_order: 100,
        tags: '',
        seo_title: '',
        seo_keywords: '',
        seo_desc: ''
      }
      this.dialogVisible = true
    },
    async handleView(row) {
      try {
        const response = await getGoodsDetail(row.id)
        this.currentGoods = response.data
        this.viewDialogVisible = true
      } catch (error) {
        console.error('获取商品详情失败:', error)
      }
    },
    async handleEdit(row) {
      try {
        const response = await getGoodsDetail(row.id)
        this.goodsForm = response.data
        this.dialogTitle = '编辑商品'
        this.dialogVisible = true
      } catch (error) {
        console.error('获取商品详情失败:', error)
      }
    },
    handleDelete(row) {
      this.$confirm('确认删除该商品吗？删除后不可恢复！', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteGoods(row.id)
          this.$message.success('删除成功')
          this.fetchGoodsList()
        } catch (error) {
          console.error('删除商品失败:', error)
        }
      })
    },
    handleSubmit() {
      this.$refs.goodsForm.validate(async (valid) => {
        if (valid) {
          try {
            // 构建提交数据，只包含后端需要的字段
            const submitData = {
              goods_name: this.goodsForm.goods_name,
              goods_sub_title: this.goodsForm.goods_sub_title,
              goods_code: this.goodsForm.goods_code,
              shop_id: this.goodsForm.shop_id,
              category_id: this.goodsForm.category_id,
              goods_images: this.goodsForm.goods_images,
              goods_video: this.goodsForm.goods_video,
              goods_desc: this.goodsForm.goods_desc,
              price: this.goodsForm.price,
              original_price: this.goodsForm.original_price,
              cost_price: this.goodsForm.cost_price,
              stock: this.goodsForm.stock,
              min_stock: this.goodsForm.min_stock,
              weight: this.goodsForm.weight,
              volume: this.goodsForm.volume,
              shipping_fee: this.goodsForm.shipping_fee,
              free_shipping: this.goodsForm.free_shipping,
              status: this.goodsForm.status,
              is_recommend: this.goodsForm.is_recommend,
              is_new: this.goodsForm.is_new,
              is_hot: this.goodsForm.is_hot,
              is_best: this.goodsForm.is_best,
              sort_order: this.goodsForm.sort_order,
              tags: this.goodsForm.tags,
              seo_title: this.goodsForm.seo_title,
              seo_keywords: this.goodsForm.seo_keywords,
              seo_desc: this.goodsForm.seo_desc
            }

            // 如果是编辑，添加ID
            if (this.goodsForm.id) {
              submitData.id = this.goodsForm.id
              await updateGoods(submitData)
              this.$message.success('更新成功')
            } else {
              await createGoods(submitData)
              this.$message.success('创建成功')
            }
            this.dialogVisible = false
            this.fetchGoodsList()
          } catch (error) {
            console.error('操作失败:', error)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.goods-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 8px;
  font-size: 12px;
  border: none;
  background: none;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}

.dialog-footer {
  text-align: right;
}

.goods-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
}

.detail-table {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #EBEEF5;
  min-height: 50px;
  align-items: center;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 150px;
  text-align: right;
  padding: 15px 20px;
  background-color: #FAFAFA;
  border-right: 1px solid #EBEEF5;
  flex-shrink: 0;
  box-sizing: border-box;
}

.detail-value {
  color: #303133;
  word-break: break-word;
  padding: 15px 20px;
  flex: 1;
  box-sizing: border-box;
}

.goods-detail .price {
  color: #f56c6c;
  font-size: 18px;
  font-weight: bold;
}

.goods-detail .original-price {
  color: #999;
  text-decoration: line-through;
}

.goods-detail .stat-value {
  color: #409EFF;
  font-size: 16px;
  font-weight: 600;
}

.goods-images {
  display: flex;
  flex-wrap: wrap;
}

/* 商品图片上传样式 */
.goods-images-upload {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  /* 空间不够时把右侧提示挤到下一行,而不是让上传区横向撑破对话框 */
  flex-wrap: wrap;
}

/* el-upload 根节点是 inline-block,作为 flex 项时宽度按 shrink-to-fit 算,
   等于"所有缩略图并排一行"的 max-content(每张 148px + 8px 右边距 = 156px)。
   原来这里写 flex-shrink:0 锁死不收缩,7 张就是 1092px,
   而对话框 1000px 减去 body padding 和 120px 的 label 宽度只剩 840px,必然溢出。
   改成可伸缩 + 允许收缩,里面的 ul 就会自己换行。 */
.images-uploader {
  flex: 1 1 320px;
  min-width: 0;
  max-width: 100%;
}

.upload-tips {
  /* 提示栏固定 180px,剩下的宽度全给上传区。
     840 - 20(gap) - 180 = 640,正好排 4 列缩略图(4 x 156 = 624),还有 16px 余量。
     提示文字完整一行需要 194px,和这个值只差 2px 就冲突,余量太薄不能要——
     宁可让 "WEBP 格式" 整体折到第二行,也不能让缩略图从 4 列掉到 3 列。 */
  flex: 0 0 180px;
  color: #909399;
  font-size: 12px;
  line-height: 1.8;
}

.upload-tips p {
  margin: 0;
  padding: 2px 0;
}

/* 视频上传样式 */
.video-upload-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.video-uploader {
  flex-shrink: 0;
}

.video-uploader >>> .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  width: 300px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-uploader >>> .el-upload:hover {
  border-color: #409EFF;
}

.video-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 300px;
  height: 200px;
  line-height: 200px;
  text-align: center;
}

.video-preview {
  width: 300px;
  height: 200px;
  display: block;
  object-fit: cover;
}

.video-upload-tips {
  flex: 1;
  color: #909399;
  font-size: 12px;
  line-height: 1.8;
}

.video-upload-tips p {
  margin: 0;
  padding: 2px 0;
}
</style>